// Where the aurora can be seen from, by Kp: the rule of thumb NOAA's Space
// Weather Prediction Center gives ("Tips on viewing the aurora",
// spaceweather.gov/content/tips-viewing-aurora):
//
//   "At Kp = 0, the equator ward edge of the auroral oval is approximately
//   66 degrees. And it moves equatorward about 2 degrees for each level of
//   Kp ... until reaching Kp of 9 at 48 degrees magnetic latitude."
//   "the aurora can often be observed hundreds of kilometers (miles)
//   equatorward of the actual aurora"
//
// So the oval's edge is 66 - 2 Kp degrees of geomagnetic latitude, and we
// take the aurora as seen low on the poleward horizon from up to
// VIEW_REACH_KM (500 km, about 4.5 degrees) further equatorward. NOAA notes
// the relation is approximate and an average; the pages say so too.
//
// Geomagnetic latitude is corrected geomagnetic (AACGM-v2, Shepherd 2014)
// at 110 km from the grid in cgmgrid.js, which follows the real field: a
// tilted dipole puts the UK about five degrees too far poleward.
//
// Pure and shared: the /aurora/ pages, the alert job and the tests.

import { CGM_DATA, CGM_GRID } from './cgmgrid.js';

export const OVAL_EDGE_KP0 = 66;
export const OVAL_EDGE_PER_KP = 2;
export const VIEW_REACH_KM = 500;
export const KM_PER_DEG = 111.2;
export const VIEW_REACH_DEG = VIEW_REACH_KM / KM_PER_DEG;
// NOAA: "you can see very nice auroral displays even with low geomagnetic
// activity (Kp = 3 or 4)": the least an alert is sent for.
export const ALERT_MIN_KP = 3;
// The dipole pole the viewer's aurora is placed round (IGRF 2025).
export const DIPOLE_POLE = { lat: 80.85, lon: -72.6 };

const DEG = Math.PI / 180;

let grid = null;
function cgmValues() {
  if (grid) return grid;
  let bytes;
  if (typeof Buffer !== 'undefined') bytes = Buffer.from(CGM_DATA, 'base64');
  else {
    const bin = atob(CGM_DATA);
    bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  }
  grid = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  return grid;
}

// AACGM-v2 latitude (degrees, signed) at a place, bilinear on the grid; null
// near the magnetic equator, where it is undefined and no aurora is seen.
export function cgmLatitude(lat, lon) {
  const g = CGM_GRID;
  const v = cgmValues();
  const fy = Math.min(Math.max((lat - g.lat0) / g.latStep, 0), g.rows - 1);
  const x = ((((lon - g.lon0) % 360) + 360) % 360) / g.lonStep;
  const y0 = Math.min(Math.floor(fy), g.rows - 2);
  const x0 = Math.floor(x) % g.cols;
  const x1 = (x0 + 1) % g.cols;
  const ty = fy - y0;
  const tx = x - Math.floor(x);
  const at = (row, col) => v[row * g.cols + col];
  const corners = [[at(y0, x0), (1 - tx) * (1 - ty)], [at(y0, x1), tx * (1 - ty)], [at(y0 + 1, x0), (1 - tx) * ty], [at(y0 + 1, x1), tx * ty]];
  let sum = 0;
  let w = 0;
  let sign = 0;
  for (const [val, wt] of corners) {
    if (val === -32768) continue;
    sum += val * wt;
    w += wt;
    sign += Math.sign(val) * wt;
  }
  // Every corner must be defined and on one side of the equator.
  if (w < 0.999 || Math.abs(sign) < w * 0.999) return null;
  return sum / w / 100;
}

// Centred dipole latitude, the viewer's frame for the oval.
export function dipoleLatitude(lat, lon, pole = DIPOLE_POLE) {
  const s = Math.sin(lat * DEG) * Math.sin(pole.lat * DEG)
    + Math.cos(lat * DEG) * Math.cos(pole.lat * DEG) * Math.cos((lon - pole.lon) * DEG);
  return Math.asin(Math.max(-1, Math.min(1, s))) / DEG;
}

// The oval's equatorward edge for a Kp (NOAA), in geomagnetic latitude.
export function ovalEdge(kp) {
  return OVAL_EDGE_KP0 - OVAL_EDGE_PER_KP * Math.max(0, Math.min(9, kp));
}

// The Kp at which the aurora reaches overhead and the horizon of a place
// at geomagnetic latitude mlat; above 9 means never on this rule.
export function kpNeeded(mlat) {
  const a = Math.abs(mlat);
  const round = (k) => Math.max(0, Math.round(k * 10) / 10);
  return {
    overhead: round((OVAL_EDGE_KP0 - a) / OVAL_EDGE_PER_KP),
    horizon: round((OVAL_EDGE_KP0 - VIEW_REACH_DEG - a) / OVAL_EDGE_PER_KP),
  };
}

// 'overhead' | 'horizon' | 'unlikely' for a place at mlat under Kp.
export function viewLevel(mlat, kp) {
  if (mlat === null || mlat === undefined) return 'unlikely';
  const a = Math.abs(mlat);
  const edge = ovalEdge(kp);
  if (a >= edge) return 'overhead';
  if (a >= edge - VIEW_REACH_DEG) return 'horizon';
  return 'unlikely';
}

// The geographic latitude of the view line (NOAA's rule plus the reach)
// along each longitude, per hemisphere, for the maps: where the geomagnetic
// latitude crosses the line, searched on the CGM grid.
export function viewLine(kp, { hemisphere = 1, stepLon = 4, reach = true } = {}) {
  const target = ovalEdge(kp) - (reach ? VIEW_REACH_DEG : 0);
  const pts = [];
  for (let lon = -180; lon <= 180; lon += stepLon) {
    let found = null;
    // From the pole toward the equator, the first latitude under the line.
    for (let lat = 89; lat >= 15; lat -= 0.5) {
      const m = cgmLatitude(hemisphere * lat, lon);
      if (m === null) continue;
      if (Math.abs(m) < target) { found = hemisphere * (lat + 0.25); break; }
    }
    if (found !== null) pts.push([lon, found]);
  }
  return pts;
}

// The Sun: declination and Greenwich hour angle, Astronomical Almanac low
// precision (good to 0.01 degree from 1950 to 2050).
export function sunPosition(ms) {
  const n = ms / 86400000 + 2440587.5 - 2451545.0;
  const L = (280.46 + 0.9856474 * n) % 360;
  const g = ((357.528 + 0.9856003 * n) % 360) * DEG;
  const lambda = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * DEG;
  const eps = (23.439 - 0.0000004 * n) * DEG;
  const ra = Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda)) / DEG;
  const dec = Math.asin(Math.sin(eps) * Math.sin(lambda)) / DEG;
  const gmst = (280.46061837 + 360.98564736629 * n) % 360;
  // The sub-solar point.
  const lon = ((((ra - gmst) % 360) + 540) % 360) - 180;
  return { dec, lat: dec, lon };
}

// Greenwich mean sidereal time, degrees (the Earth's turn under the stars).
export function gmstDeg(ms) {
  const n = ms / 86400000 + 2440587.5 - 2451545.0;
  return (((280.46061837 + 360.98564736629 * n) % 360) + 360) % 360;
}

// The Sun's altitude (degrees) from a place.
export function sunAltitude(lat, lon, ms) {
  const s = sunPosition(ms);
  const h = (lon - s.lon) * DEG;
  const sinAlt = Math.sin(lat * DEG) * Math.sin(s.dec * DEG) + Math.cos(lat * DEG) * Math.cos(s.dec * DEG) * Math.cos(h);
  return Math.asin(Math.max(-1, Math.min(1, sinAlt))) / DEG;
}

// The next spell of darkness (the Sun more than `below` degrees down) that
// starts within `hours` of fromMs, or is under way then: { start, end,
// middle } in ms, or null when it does not get that dark (high-latitude
// summer). Sampled every 10 minutes, then the edges to a minute.
export function nextDark(lat, lon, fromMs, { hours = 30, below = -12 } = {}) {
  const step = 10 * 60000;
  const dark = (t) => sunAltitude(lat, lon, t) < below;
  const edge = (a, b) => {
    // a and b differ: bisect to a minute.
    const da = dark(a);
    while (b - a > 60000) { const m = (a + b) / 2; if (dark(m) === da) a = m; else b = m; }
    return Math.round(b / 60000) * 60000;
  };
  let start = null;
  if (dark(fromMs)) start = fromMs;
  else {
    for (let t = fromMs; t < fromMs + hours * 3600000; t += step) {
      if (dark(t + step)) { start = edge(t, t + step); break; }
    }
  }
  if (start === null) return null;
  let end = start + 20 * 3600000;
  for (let t = start; t < start + 20 * 3600000; t += step) {
    if (!dark(t + step)) { end = edge(t, t + step); break; }
  }
  return { start, end, middle: Math.round((start + end) / 2) };
}

// Tonight's outlook for a place from a Kp series (parse.js parseKp):
// the darkness, the most Kp predicted in it, and what that means there.
export function nightOutlook(place, kpSeries, fromMs = Date.now(), { below = -12 } = {}) {
  const mlat = cgmLatitude(place.lat, place.lon);
  const need = mlat === null ? null : kpNeeded(mlat);
  const night = nextDark(place.lat, place.lon, fromMs, { below });
  let peak = null;
  if (night && Array.isArray(kpSeries)) {
    for (const b of kpSeries) {
      const t = Date.parse(b.t);
      if (t + 3 * 3600000 <= night.start || t >= night.end) continue;
      if (!peak || b.kp > peak.kp) peak = b;
    }
  }
  const level = peak && mlat !== null ? viewLevel(mlat, peak.kp) : 'unlikely';
  return { mlat, need, night, peak, level };
}

// Whether the daily alert job should email a place about its coming night:
// dark, a forecast Kp that brings the aurora at least to its horizon, and
// at least NOAA's "Kp = 3 or 4" for a display worth going out for.
// A night already under way with under three hours of darkness left is
// passed over for the next one (the job runs once a day, 14:30 UT, which is
// the small hours in New Zealand). `night` names the night by the local
// (mean solar) date its darkness starts on, so a place hears once a night.
export function alertDecision(place, kpSeries, fromMs = Date.now()) {
  let o = nightOutlook(place, kpSeries, fromMs);
  if (o.night && o.night.end - fromMs < 3 * 3600000) o = nightOutlook(place, kpSeries, o.night.end + 60000);
  const ok = Boolean(o.night && o.peak && o.mlat !== null && o.level !== 'unlikely' && o.peak.kp >= ALERT_MIN_KP);
  const night = o.night ? new Date(o.night.start + (place.lon / 15) * 3600000).toISOString().slice(0, 10) : null;
  return { ...o, night: o.night, nightOf: night, send: ok };
}
